import React, { useState } from 'react';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { usePartnerApiKeysQuery } from '@hooks/requests/queries/use_partner_api_keys_query';
import type { PartnerApiKey } from '@hooks/requests/queries/use_partner_api_keys_query';
import { useToast } from '@hooks/useToast';
import {
  ApiKeysTable,
  CreateApiKeyModal,
  RenameApiKeyModal,
  RevokeApiKeyConfirmationModal,
} from '@components/api-keys';

/**
 * API Keys (sc-45102 "Show existing keys (1 of 3)", sc-45103 "Delete and
 * create a key (2 of 3)"). Figma "API Keys (30/07)" — populated: 2860:13126,
 * empty: 2992:9634, error: 3084:13788.
 *
 * `AdminRoute` (routes/index.tsx) already refuses the page to anyone but
 * AceUp/Partner Admins (AC1) and `Sidebar` already hides the nav item for
 * both viewer roles (AC2), so by the time this component renders, the
 * caller is known to be an admin.
 *
 * The 1-key floor to revoke (AC3) is pre-checked here from the already-
 * loaded key list — no extra request — before that modal opens. The 2-key
 * cap (AC9) is instead enforced up front by `ApiKeysTable` disabling "New
 * Key" itself once the list hits the limit (Figma 3079:10374), so this page
 * no longer needs its own pre-click check for it. Both gates are still
 * enforced again by the backend regardless (AC14's race), which is what the
 * 409 `key_limit_reached` handling in `CreateApiKeyModal` is for.
 *
 * Rename (sc-45257 "Rename an existing key (3 of 4)") has no such gate —
 * it never changes how many keys the account has — so `renameTarget` just
 * tracks which row's menu opened the modal, the same shape `revokeTarget`
 * already uses.
 */
const ApiKeysPage: React.FC = () => {
  const { isAdmin, isLoading: rolesLoading } = usePartnerRoles();
  const keysQuery = usePartnerApiKeysQuery({ enabled: isAdmin && !rolesLoading });
  const { showToast } = useToast();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<PartnerApiKey | null>(null);
  const [renameTarget, setRenameTarget] = useState<PartnerApiKey | null>(null);

  const keys = keysQuery.data ?? [];

  const handleCreateKeyClick = () => setIsCreateModalOpen(true);

  return (
    <div className="api-keys-page">
      <div className="api-keys-header">
        <h1 className="api-keys-title">API Keys</h1>
        <p className="api-keys-subtitle">
          Create and manage API keys to authenticate requests to Ally Connect
        </p>
      </div>

      {/* sc-46030: new Design System card — the "API KEYS" eyebrow is gone
          (the page title already names the section). */}
      <section className="api-keys-card" aria-label="API keys">
        <div className="api-keys-card-body">
          <ApiKeysTable
            keys={keys}
            isLoading={rolesLoading || keysQuery.isLoading}
            error={keysQuery.error}
            onRetry={() => keysQuery.refetch()}
            onCreateKey={handleCreateKeyClick}
            onRevokeKey={setRevokeTarget}
            onRevokeBlockedByFloor={() =>
              showToast('You need a second key before you can delete this one.', 'error')
            }
            onRenameKey={setRenameTarget}
          />
        </div>
      </section>

      <CreateApiKeyModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      <RevokeApiKeyConfirmationModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        apiKey={revokeTarget}
      />
      <RenameApiKeyModal
        isOpen={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        apiKey={renameTarget}
      />
    </div>
  );
};

export default ApiKeysPage;
